import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useBreakpointValue,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FaTrash, FaPencilAlt, FaRedhat, FaPlus } from "react-icons/fa";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { Link } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import axios from "../../axiosConfig";
import Cookies from "js-cookie";
import ListPageLayout from "../common/ListPageLayout";
import TruncatedText from "../common/TruncatedText";
import ContextUserCreateModal from "./ContextUserCreateModal";
import PaginatedListToolbar from "../common/PaginatedListToolbar";
import PaginationControls from "../common/PaginationControls";
import usePaginatedList from "../common/usePaginatedList";
import { getDisplayRoleLabel } from "../../utils/displayRole";
import { getDefaultAuthenticatedRoute, getUserRoles } from "../../utils/authRoles";
import { getDeleteUserSafety } from "../../utils/rbacSafety";
import { getMutationError, getUserMutationToast } from "../../utils/userMutationFeedback";
import {
  canAccessUserInContext,
  canCreateUsersInContext,
  canSimulateUser,
  filterUsersForPolicy,
} from "../../utils/userCreationPermissions";

const roleMap = {
  tech: "Tech",
  admin: "Admin",
  user: "User",
  org_user: "School User",
  organization_user: "School User",
};

const NAME_MAX_CHARS = 80;
const EMAIL_MAX_CHARS = 80;
const MAX_VISIBLE_ROLE_BADGES = 2;
const ORGANIZATION_NAME_MAX_CHARS = 25;

const USER_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "removed", label: "Removed" },
  { value: "all", label: "All" },
];

const getRbacAssignments = (u) => (Array.isArray(u?.rbac?.assignments) ? u.rbac.assignments : []);

const labelForAssignment = (a) => {
  const roleName = (a?.role_name ?? a?.role?.name ?? a?.name ?? "").toString().trim();
  const scopedName = (
    a?.organization?.name ??
    a?.org?.name ??
    a?.consultant?.name ??
    a?.network?.name ??
    a?.organization_name ??
    a?.org_name ??
    a?.consultant_name ??
    a?.network_name ??
    a?.scope?.name ??
    a?.scope_name ??
    ""
  )
    .toString()
    .trim();

  const displayRoleName = roleName ? getDisplayRoleLabel(roleName) : "";

  if (displayRoleName && scopedName) return `${displayRoleName} @ ${scopedName}`;
  if (displayRoleName) return displayRoleName;
  if (scopedName) return scopedName;
  return "Unknown Role";
};

const getShortRoleLabel = (assignment) => {
  const roleName = (assignment?.role_name ?? assignment?.role?.name ?? assignment?.name ?? "")
    .toString()
    .trim();
  const normalizedRoleName = roleName.toLowerCase();

  const shortRoleMap = {
    consultant_admin: "Consultant",
    consultant_advisor: "Advisor",
    network_manager: "Network Admin",
    network_viewer: "Network Viewer",
    organization_admin: "Org Admin",
    organization_user: "Org User",
    org_user: "Org User",
    hke_admin: "HKE Admin",
  };

  if (shortRoleMap[normalizedRoleName]) return shortRoleMap[normalizedRoleName];

  const fallbackLabel = roleName ? getDisplayRoleLabel(roleName) : "";
  if (!fallbackLabel) return "Unknown Role";

  return fallbackLabel
    .replace(/\bOrganization\b/g, "Org")
    .replace(/^Consultant\s+/i, "")
    .trim();
};

const getOrganizationMemberships = (theUser) =>
  Array.isArray(theUser?.organization_memberships) ? theUser.organization_memberships : [];

const getOrganizationMembershipRoleLabel = (contextRole) =>
  contextRole === "admin" ? "School Admin" : "School User";

const getUserStatus = (theUser) =>
  theUser?.status === "removed" ? "removed" : "active";

const getStatusLabel = (status) => (status === "removed" ? "Removed" : "Active");

const getStatusColorScheme = (status) => (status === "removed" ? "red" : "green");

const truncateText = (value, maxChars) => {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized) return "";
  if (normalized.length <= maxChars) return normalized;
  return `${normalized.slice(0, Math.max(0, maxChars - 3))}...`;
};

const colorForRoleName = (name) => {
  const n = (name ?? "").toString().trim().toLowerCase();

  if (n.includes("tech")) return "purple";
  if (n.includes("admin")) return "orange";
  if (n.includes("consultant")) return "teal";
  if (n.includes("network")) return "cyan";
  if (n.includes("org")) return "blue";
  if (n.includes("user")) return "blue";

  return "gray";
};

const normalizeActionName = (value) => (typeof value === "string" ? value.trim().toLowerCase() : null);

const getAllowedActions = (theUser) =>
  Array.isArray(theUser?.allowed_actions)
    ? theUser.allowed_actions.map(normalizeActionName).filter(Boolean)
    : [];

const UserList = ({
  context = { type: "hke" },
  basePath = "/app/users",
  title = "Users",
  description = "Manage user accounts and access.",
  scope = "hke",
  allowGlobalCreate = true,
}) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const createUserModal = useDisclosure();
  const toast = useToast();

  const { user, setUser } = useUser();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isConsultantScoped = scope === "consultant";
  const isNetworkScoped = scope === "network";
  const currentUserRoles = getUserRoles(user);
  const isHkeAdmin = currentUserRoles.includes("hke_admin");
  const isConsultantAdmin = currentUserRoles.includes("consultant_admin");
  const resolvedContext = useMemo(() => {
    const baseContext =
      context?.type === "consultant" && isConsultantScoped
        ? { ...context, namespace: "consultant" }
        : context;

    if (
      baseContext?.type === "consultant" &&
      !(Number(baseContext?.id) > 0) &&
      Number(user?.consultant_id) > 0
    ) {
      return { ...baseContext, id: Number(user.consultant_id) };
    }

    return baseContext;
  }, [context, isConsultantScoped, user?.consultant_id]);
  const canCreateInContext =
    allowGlobalCreate &&
    (resolvedContext?.type === "hke" || Number(resolvedContext?.id) > 0) &&
    canCreateUsersInContext(user, resolvedContext);
  const visibleUsers = useMemo(
    () => filterUsersForPolicy(users, user, resolvedContext),
    [resolvedContext, user, users]
  );

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const subTextColor = useColorModeValue("gray.400", "gray.200");

  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const shouldIncludeRemoved = statusFilter === "removed" || statusFilter === "all";
      const response = await axios.get("/users", {
        params: shouldIncludeRemoved ? { include_removed: true } : undefined,
      });
      const fetchedUsers = response.data?.users ?? [];
      setUsers(
        statusFilter === "removed"
          ? fetchedUsers.filter((targetUser) => getUserStatus(targetUser) === "removed")
          : fetchedUsers
      );
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const getDeleteSafety = useCallback(
    (targetUser) => getDeleteUserSafety(targetUser, visibleUsers),
    [visibleUsers]
  );

  const handleDelete = async (targetUser) => {
    if (targetUser?.id === user?.id) {
      toast({
        title: "Delete not allowed",
        description: "You cannot delete yourself.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const deleteSafety = getDeleteSafety(targetUser);
    if (deleteSafety.isBlocked) {
      toast({
        title: "Delete not allowed",
        description: deleteSafety.blockReason,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!window.confirm(deleteSafety.confirmationMessage)) return;

    try {
      await axios.delete(`/users/${targetUser.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== targetUser.id));
      toast(getUserMutationToast("delete"));
    } catch (error) {
      const { message } = getMutationError(error);
      toast({
        title: "Could not delete user",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error("Error deleting user:", error);
    }
  };

  const canEditUser = (theUser) => {
    if (!user || theUser.id === user.id) return false;

    const allowedActions = getAllowedActions(theUser);
    if (allowedActions.length > 0) {
      return allowedActions.includes("edit") || allowedActions.includes("update") || allowedActions.includes("manage");
    }

    if (typeof theUser?.can_edit === "boolean") return theUser.can_edit;
    if (typeof theUser?.editable === "boolean") return theUser.editable;

    if (isHkeAdmin) return true;
    if ((isConsultantScoped && isConsultantAdmin) || (isNetworkScoped && canCreateInContext)) {
      return canAccessUserInContext(user, theUser, resolvedContext);
    }

    return false;
  };

  const canDeleteUser = (theUser) => {
    if (!user || theUser.id === user.id) return false;

    const allowedActions = getAllowedActions(theUser);
    if (allowedActions.length > 0) {
      return allowedActions.includes("delete") || allowedActions.includes("destroy") || allowedActions.includes("remove");
    }

    if (typeof theUser?.can_delete === "boolean") return theUser.can_delete;
    if (typeof theUser?.deletable === "boolean") return theUser.deletable;

    if (isHkeAdmin) return true;
    if (isNetworkScoped && canCreateInContext) return canAccessUserInContext(user, theUser, resolvedContext);
    return false;
  };

  const canSimulateTargetUser = (theUser) => canSimulateUser(user, theUser);

  const handleSimulate = async (userId) => {
    const confirmed = window.confirm("Are you sure you want to simulate this user?");
    if (!confirmed) return;

    try {
      const res = await axios.post("/sessions/simulate", { user_id: userId });

      const token = res.headers.authorization?.split(" ")[1];

      if (token) {
        localStorage.setItem("hkeAuthToken", token);
        localStorage.setItem("tokenCreationTime", Date.now().toString());
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        Cookies.set("simulatedMode", "true", { secure: false, sameSite: "Lax", path: "/" });

        if (res.data.user) {
          setUser({ ...res.data.user, is_simulated: true });
        }

        window.location.href = getDefaultAuthenticatedRoute(res.data.user);
      } else {
        alert("Simulation token not received.");
      }
    } catch (err) {
      alert("Simulation failed. Please try again.");
    }
  };

  const fullName = (u) =>
    `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();

  const roleSortLabel = (u) => {
    const assignments = getRbacAssignments(u);
    if (assignments.length > 0) {
      return assignments.map(labelForAssignment).join(" | ");
    }
    return roleMap[u?.role] || u?.role || "";
  };
  const statusSortLabel = (u) => getStatusLabel(getUserStatus(u));
  const userSortOptions = useMemo(
    () => [
      { key: "name", label: "Name", getSortValue: fullName },
      { key: "email", label: "Email", getSortValue: (targetUser) => targetUser.email },
      { key: "role", label: "Role", getSortValue: roleSortLabel },
      { key: "status", label: "Status", getSortValue: statusSortLabel },
    ],
    []
  );

  const {
    paginatedItems: paginatedUsers,
    totalCount,
    totalPages,
    currentPage,
    pageSize,
    searchText,
    sortKey,
    sortDir,
    setCurrentPage,
    setPageSize,
    setSearchText,
    setSortDir,
    setSortKey,
    toggleSort,
  } = usePaginatedList({
    items: visibleUsers,
    sortOptions: userSortOptions,
    initialSortKey: "name",
    initialSortDir: "asc",
    initialPageSize: 25,
    searchMatcher: (targetUser, normalizedSearch) => {
      const haystacks = [
        fullName(targetUser),
        targetUser?.email,
        roleSortLabel(targetUser),
        ...getRbacAssignments(targetUser).map(labelForAssignment),
      ];

      return haystacks.some((value) => value?.toString().toLowerCase().includes(normalizedSearch));
    },
  });

  const SortIndicator = ({ active }) => {
    if (!active) return null;
    return sortDir === "asc" ? (
      <TriangleUpIcon ml={2} boxSize="10px" />
    ) : (
      <TriangleDownIcon ml={2} boxSize="10px" />
    );
  };

  /* ---------------- UI bits ---------------- */

  const RbacBadges = ({ user: theUser }) => {
    const assignments = getRbacAssignments(theUser);

    if (assignments.length === 0) {
      const fallbackLabel = roleMap[theUser?.role] || "Unknown Role";
      return (
        <Badge
          variant="subtle"
          colorScheme={colorForRoleName(fallbackLabel)}
          borderRadius="md"
        >
          {fallbackLabel}
        </Badge>
      );
    }

    const mapped = assignments.map((a, idx) => {
      const roleName = a?.role_name ?? a?.role?.name ?? "";
      return {
        key: `${a?.id ?? roleName}-${idx}`,
        shortLabel: getShortRoleLabel(a),
        fullLabel: labelForAssignment(a),
        colorScheme: colorForRoleName(roleName),
      };
    });

    const visible = mapped.slice(0, MAX_VISIBLE_ROLE_BADGES);
    const hidden = mapped.slice(MAX_VISIBLE_ROLE_BADGES);

    return (
      <HStack spacing={2} flexWrap="wrap">
        {visible.map((item) => (
          <Tooltip key={item.key} label={item.fullLabel} hasArrow>
            <Badge
              variant="subtle"
              colorScheme={item.colorScheme}
              borderRadius="md"
              cursor="help"
            >
              {item.shortLabel}
            </Badge>
          </Tooltip>
        ))}
        {hidden.length > 0 && (
          <Tooltip label={hidden.map((item) => item.fullLabel).join(", ")} hasArrow>
            <Badge variant="subtle" colorScheme="gray" borderRadius="md" cursor="help">
              +{hidden.length}
            </Badge>
          </Tooltip>
        )}
      </HStack>
    );
  };

  const renderDeleteButton = (theUser, { iconOnly = false } = {}) => {
    const deleteSafety = getDeleteSafety(theUser);
    const isDisabled = deleteSafety.isBlocked;
    const tooltipLabel = isDisabled
      ? deleteSafety.blockReason
      : deleteSafety.warnings.join(" ");

    if (iconOnly) {
      const button = (
        <IconButton
          icon={<FaTrash />}
          aria-label="Delete User"
          title="Delete User"
          variant="outline"
          colorScheme="red"
          size="xs"
          onClick={() => handleDelete(theUser)}
          isDisabled={isDisabled}
        />
      );

      return tooltipLabel ? (
        <Tooltip label={tooltipLabel} hasArrow>
          <Box as="span">{button}</Box>
        </Tooltip>
      ) : (
        button
      );
    }

    const button = (
      <Button
        leftIcon={<FaTrash />}
        size="xs"
        colorScheme="red"
        variant="outline"
        onClick={() => handleDelete(theUser)}
        isDisabled={isDisabled}
      >
        Delete
      </Button>
    );

    return tooltipLabel ? (
      <Tooltip label={tooltipLabel} hasArrow>
        <Box as="span">{button}</Box>
      </Tooltip>
    ) : (
      button
    );
  };

  const StatusBadge = ({ user: theUser }) => {
    const status = getUserStatus(theUser);
    return (
      <Badge
        colorScheme={getStatusColorScheme(status)}
        variant="subtle"
        borderRadius="md"
        data-status={status}
      >
        {getStatusLabel(status)}
      </Badge>
    );
  };

  const OrganizationMemberships = ({ user: theUser }) => {
    const memberships = getOrganizationMemberships(theUser);

    if (memberships.length === 0) {
      return (
        <Text fontSize="sm" fontStyle="italic" color={subTextColor}>
          No school membership
        </Text>
      );
    }

    return (
      <VStack align="start" spacing={1} maxW="100%">
        {memberships.map((membership, index) => (
          <Tooltip
            key={`${membership.organization_id ?? membership.organization_name ?? "org"}-${index}`}
            label={membership.organization_name || "School"}
            hasArrow
          >
            <Text
              fontSize="sm"
              maxW="100%"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {truncateText(membership.organization_name || "School", ORGANIZATION_NAME_MAX_CHARS)} (
              {getOrganizationMembershipRoleLabel(membership.context_role)})
            </Text>
          </Tooltip>
        ))}
      </VStack>
    );
  };

  const renderUserCard = (u) => (
    <Box
      key={u.id}
      p={4}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      shadow="sm"
      bg={cardBg}
      mb={4}
      _hover={{ shadow: "md" }}
    >
      <VStack align="start" spacing={3} w="100%">
        <HStack justify="space-between" w="100%" align="start">
          <Box minW={0} w="100%">
            <TruncatedText
              value={fullName(u) || "—"}
              maxChars={NAME_MAX_CHARS}
              fontWeight="bold"
              fontSize="md"
            />
            <TruncatedText
              value={u.email || "—"}
              maxChars={EMAIL_MAX_CHARS}
              fontSize="sm"
              color={subTextColor}
            />
          </Box>

          <RbacBadges user={u} />
        </HStack>

        <Box w="100%">
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={subTextColor} mb={1}>
            Status
          </Text>
          <StatusBadge user={u} />
        </Box>

        <Box w="100%">
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color={subTextColor} mb={1}>
            Schools
          </Text>
          <OrganizationMemberships user={u} />
        </Box>

        {(canEditUser(u) || canDeleteUser(u) || canSimulateTargetUser(u)) && (
          <HStack w="100%" justify="flex-end" spacing={2} flexWrap="wrap">
            {canEditUser(u) ? (
              <Button
                as={Link}
                to={`${basePath}/edit/${u.id}`}
                leftIcon={<FaPencilAlt />}
                size="xs"
                variant="outline"
                colorScheme="yellow"
              >
                Edit
              </Button>
            ) : null}

            {canDeleteUser(u) ? renderDeleteButton(u) : null}

            {canSimulateTargetUser(u) ? (
              <Button
                leftIcon={<FaRedhat />}
                size="xs"
                colorScheme="purple"
                variant="outline"
                onClick={() => handleSimulate(u.id)}
              >
                Simulate
              </Button>
            ) : null}
          </HStack>
        )}
      </VStack>
    </Box>
  );

  const renderUserTable = () => (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      shadow="sm"
      overflow="hidden"
    >
      <TableContainer w="100%" maxW="100%" minW={0} overflowX="auto">
        <Table
          variant="unstyled"
          size="md"
          tableLayout="fixed"
          w="100%"
          maxW="100%"
          sx={{
            "th, td": { borderBottom: "1px solid", borderColor: "gray.100" },
            "tbody tr:hover": { background: "gray.50" },
          }}
        >
          <colgroup>
            <col style={{ width: "18%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>

          <Thead>
            <Tr
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: cardBg,
              }}
            >
              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("name")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Name <SortIndicator active={sortKey === "name"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("email")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Email <SortIndicator active={sortKey === "email"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("role")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Role <SortIndicator active={sortKey === "role"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("status")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Status <SortIndicator active={sortKey === "status"} />
              </Th>

              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Schools
              </Th>

              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Actions
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {paginatedUsers.length > 0 ? (
              paginatedUsers.map((u) => (
                <Tr key={u.id}>
                  <Td py={3}>
                    <Box minW={0} w="100%">
                      <TruncatedText
                        value={fullName(u) || "—"}
                        maxChars={NAME_MAX_CHARS}
                        fontWeight="semibold"
                      />
                    </Box>
                  </Td>

                  <Td py={3}>
                    <Box minW={0} w="100%">
                      <TruncatedText value={u.email || "—"} maxChars={EMAIL_MAX_CHARS} />
                    </Box>
                  </Td>

                  <Td py={3}>
                    <RbacBadges user={u} />
                  </Td>

                  <Td py={3}>
                    <StatusBadge user={u} />
                  </Td>

                  <Td py={3}>
                    <Box maxW="240px" minW={0}>
                      <OrganizationMemberships user={u} />
                    </Box>
                  </Td>

                  <Td py={3}>
                    {(canEditUser(u) || canDeleteUser(u) || canSimulateTargetUser(u)) && (
                      <HStack spacing={2} flexWrap="wrap">
                        {canEditUser(u) ? (
                          <Tooltip label="Edit" hasArrow>
                            <IconButton
                              as={Link}
                              to={`${basePath}/edit/${u.id}`}
                              icon={<FaPencilAlt />}
                              aria-label="Edit User"
                              title="Edit User"
                              variant="outline"
                              size="xs"
                              colorScheme="yellow"
                            />
                          </Tooltip>
                        ) : null}

                        {canDeleteUser(u) ? renderDeleteButton(u, { iconOnly: true }) : null}

                        {canSimulateTargetUser(u) ? (
                          <Tooltip label="Simulate" hasArrow>
                            <IconButton
                              icon={<FaRedhat />}
                              aria-label="Simulate User"
                              title="Simulate User"
                              variant="outline"
                              colorScheme="purple"
                              size="xs"
                              onClick={() => handleSimulate(u.id)}
                            />
                          </Tooltip>
                        ) : null}
                      </HStack>
                    )}
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={6}>
                  <Text textAlign="center">
                    {statusFilter === "removed" ? "No removed users" : "No users found."}
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  // Render a loading message if userData is not yet available
  if (!user) {
    return <Text>Loading...</Text>;
  }

  const actions = (
    <HStack spacing={3} flexWrap="wrap">
      {canCreateInContext && (
        <Button
          leftIcon={<FaPlus />}
          onClick={createUserModal.onOpen}
          colorScheme="green"
          variant="outline"
          size="sm"
        >
          Add User
        </Button>
      )}
    </HStack>
  );

  return (
    <ListPageLayout title={title} description={description} actions={actions} contentProps={{ maxW: "100%", minW: 0 }}>
      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} mb={4}>
        <CardBody>
          <PaginatedListToolbar
            searchText={searchText}
            onSearchTextChange={setSearchText}
            searchPlaceholder="Search by name, email, or role"
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalCount={totalCount}
            sortKey={sortKey}
            sortDir={sortDir}
            sortOptions={userSortOptions}
            onSortKeyChange={setSortKey}
            onSortDirChange={setSortDir}
            extraControls={(
              <FormControl w={{ base: "calc(50% - 8px)", md: "140px" }} minW="140px">
                <FormLabel mb={1}>Status</FormLabel>
                <Select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  {USER_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </CardBody>
      </Card>

      {loadingUsers ? (
        <Flex justify="center" mt={10}>
          <Spinner size="xl" color="orange.400" />
        </Flex>
      ) : isMobile ? (
        <Box mt={2}>
          {paginatedUsers.length > 0 ? (
            paginatedUsers.map(renderUserCard)
          ) : (
            <Text textAlign="center" mt={10}>
              {statusFilter === "removed" ? "No removed users" : "No users found."}
            </Text>
          )}
        </Box>
      ) : (
        <Box mt={2}>{renderUserTable()}</Box>
      )}
      {!loadingUsers && totalCount > 0 ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      ) : null}
      <ContextUserCreateModal
        isOpen={createUserModal.isOpen}
        onClose={createUserModal.onClose}
        context={resolvedContext}
        title={isConsultantScoped ? "Add scoped user" : "Create user"}
        submitLabel="Create user"
        onCreated={fetchUsers}
      />
    </ListPageLayout>
  );
};

export default UserList;
